/**
 * Cases where we asked the court for a jury trial and no setting has come back.
 *
 * Two things the copy has to be honest about. An empty list is not "every court answered" when no
 * request dates exist on file, the same trap the overdue acknowledgment list carries. And the
 * window is the firm's rule for when to chase a quiet court, not a deadline the court owes us, so
 * the screen says which of the two it is.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DiscoveryListsApi, {
  AwaitingTrialSettingRow,
  AwaitingTrialSettingsDto,
} from '../../api/DiscoveryListsApi';

function dateLabel(value: string): string {
  return value.length >= 10 ? value.slice(0, 10) : value;
}

const ManagerAwaitingTrialSettingsView: React.FC = () => {
  const navigate = useNavigate();
  const [dto, setDto] = useState<AwaitingTrialSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDto(await DiscoveryListsApi.awaitingTrialSettings());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the jury trial setting list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: AwaitingTrialSettingRow[] = dto?.overdue ?? [];
  const datesNeverRecorded = dto != null && (dto.everRequested ?? 0) === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Waiting on a trial setting
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Cases where a jury trial was requested and the court has not sent a setting back.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : datesNeverRecorded ? (
        <Alert severity="info">
          No case has a jury trial request date on file. This list is empty because the request was
          never recorded, not because every court has answered. Recording the request is what starts
          the clock.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            As of {dto?.asOf}. {dto?.awaitingSetting} case(s) still waiting for a setting,{' '}
            {dto?.everRequested} case(s) have a request on file.
          </Typography>
          {dto?.settingDayWindow != null && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Listed after {dto.settingDayWindow} days of silence, the firm's general rule for
              asking a court for a status update. It is when to chase, not a deadline the court
              owes us.
            </Alert>
          )}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Citation</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Chase from</TableCell>
                  <TableCell>Days waiting past that</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nothing has been waiting longer than the window right now.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.ticketGuid}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ticket?Ticket_guid=${row.ticketGuid}`)}
                    >
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{row.citationNumber || '—'}</TableCell>
                      <TableCell>{row.courtName || '—'}</TableCell>
                      <TableCell>{dateLabel(row.requestedAt)}</TableCell>
                      <TableCell>{dateLabel(row.deadline)}</TableCell>
                      <TableCell>{row.daysOverdue}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default ManagerAwaitingTrialSettingsView;
